/**
 * API Client pour la gestion des avoirs (credit notes)
 * ✅ SKILL FACTURXPRO: Architecture 3 couches - API Layer
 */
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


// Types basés sur le modèle Prisma
export type CreditNoteStatus = 'DRAFT' | 'SENT' | 'APPLIED';

export interface CreditNoteItem {
  id: string;
  creditNoteId: string;
  productId?: string;
  position: number;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRateId?: string;
  taxPercent: number;
  totalHT: number;
  totalTTC: number;
  unit?: string;
  reference?: string;
  descriptionType?: 'COMMERCIAL' | 'TECHNICAL' | 'BOTH';
}

export interface CreditNote {
  id: string;
  tenantId: string;
  companyId: string;
  clientId: string;
  invoiceId: string;
  number: string;
  issueDate: string;
  reason: string;
  notes?: string;
  currencyCode: string;
  status: CreditNoteStatus;
  isLocked: boolean;
  lockedAt?: string;
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  // Relations
  Client?: {
    id: string;
    fullName?: string;
    companyName?: string;
    email?: string;
  };
  Invoice?: {
    id: string;
    number: string;
    totalTTC: number;
  };
  CreditNoteItem?: CreditNoteItem[];
}

export interface CreditNotesResponse {
  creditNotes: CreditNote[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface CreateCreditNoteDTO {
  invoiceId: string;
  clientId: string;
  reason: string;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    taxRateId: string;         // ✅ OBLIGATOIRE - Protection anti-fraude fiscale
    taxPercent: number;        // ✅ OBLIGATOIRE - Snapshot du taux (pour historique)
    position: number;          // ✅ OBLIGATOIRE - Ordre d'affichage de la ligne
    productId?: string;        // ✅ OPTIONNEL
  }>;
  issueDate?: string;
  notes?: string;
}

export interface UpdateCreditNoteDTO {
  reason?: string;
  status?: CreditNoteStatus;
  items?: Array<{
    id?: string;
    description: string;
    quantity: number;
    unitPrice: number;
    taxRateId: string;         // ✅ OBLIGATOIRE - Protection anti-fraude fiscale
    taxPercent: number;        // ✅ OBLIGATOIRE - Snapshot du taux
    productId?: string;        // ✅ OPTIONNEL
  }>;
}

/**
 * API Credit Notes
 */
export const creditNotesAPI = {
  /**
   * Récupérer tous les avoirs avec filtres optionnels
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: CreditNoteStatus;
    invoiceId?: string;
  }): Promise<CreditNotesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.invoiceId) queryParams.append('invoiceId', params.invoiceId);

    const url = `${API_URL}/billing/credit-notes${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await safeFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Erreur lors de la récupération des avoirs : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);

    // Format standard backend V2: {success: true, data: [...], meta: {...}}
    if (apiResponse.success && apiResponse.data) {
      return {
        creditNotes: Array.isArray(apiResponse.data) ? apiResponse.data : [],
        pagination: apiResponse.meta || {
          total: apiResponse.data.length || 0,
          page: 1,
          limit: apiResponse.data.length || 0,
          pages: 1,
        },
      };
    }

    // Format legacy: {creditNotes: [...], pagination: {...}}
    if (apiResponse.creditNotes) {
      return apiResponse;
    }

    // Fallback
    return {
      creditNotes: Array.isArray(apiResponse) ? apiResponse : [],
      pagination: {
        total: Array.isArray(apiResponse) ? apiResponse.length : 0,
        page: 1,
        limit: Array.isArray(apiResponse) ? apiResponse.length : 0,
        pages: 1,
      },
    };
  },

  /**
   * Récupérer un avoir par son ID
   */
  async getById(id: string): Promise<CreditNote> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération de l\'avoir');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Récupérer les avoirs d'une facture
   */
  async getByInvoice(invoiceId: string): Promise<CreditNote[]> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/invoice/${invoiceId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération des avoirs de la facture');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Créer un avoir
   */
  async create(data: CreateCreditNoteDTO): Promise<CreditNote> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la création de l\'avoir');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Mettre à jour un avoir
   */
  async update(id: string, data: UpdateCreditNoteDTO): Promise<CreditNote> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la mise à jour de l\'avoir');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Mettre à jour le statut d'un avoir
   */
  async updateStatus(id: string, status: CreditNoteStatus): Promise<CreditNote> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/${id}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la mise à jour du statut');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Appliquer un avoir (le marquer comme APPLIED)
   */
  async apply(id: string): Promise<CreditNote> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/${id}/apply`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de l\'application de l\'avoir');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Supprimer un avoir
   */
  async delete(id: string): Promise<void> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la suppression de l\'avoir');
    }
  },

  /**
   * Récupérer les statistiques des avoirs
   */
  async getStats(): Promise<{
    totalCount: number;
    totalAmount: number;
    byStatus: Record<CreditNoteStatus, { count: number; amount: number }>;
  }> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/stats`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération des statistiques');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Envoyer un avoir par email
   */
  async sendEmail(
    id: string,
    emailData: {
      recipientEmail?: string;
      customSubject?: string;
      customMessage?: string;
    }
  ): Promise<void> {
    const response = await safeFetch(`${API_URL}/billing/credit-notes/${id}/send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(emailData),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de l\'envoi de l\'email');
    }
  },
};
